import { Link, useParams } from "react-router";
import { Button, Card, EmptyState } from "@salesapp/ui";

const HTTP_ERROR_PAGES: Record<
  number,
  { title: string; description: string; action: string; href: string }
> = {
  400: {
    title: "That request was not valid",
    description: "Check what you entered and try again.",
    action: "Back to dashboard",
    href: "/",
  },
  401: {
    title: "Please sign in",
    description: "Your session is missing or has ended. Sign in to continue.",
    action: "Sign in",
    href: "/",
  },
  403: {
    title: "You do not have access",
    description: "This console is for platform staff only.",
    action: "Back to dashboard",
    href: "/",
  },
  404: {
    title: "Page not found",
    description: "That admin page does not exist or the link is incomplete.",
    action: "Back to dashboard",
    href: "/",
  },
  408: {
    title: "The request timed out",
    description: "Try again in a moment.",
    action: "Try again",
    href: "/",
  },
  409: {
    title: "This record changed",
    description: "Refresh and try again.",
    action: "Back to dashboard",
    href: "/",
  },
  410: {
    title: "That item is gone",
    description: "It was removed or is no longer available.",
    action: "Back to dashboard",
    href: "/",
  },
  413: {
    title: "That file is too large",
    description: "Use a smaller file and try again.",
    action: "Back to dashboard",
    href: "/",
  },
  422: {
    title: "Some fields need attention",
    description: "Fix the highlighted information and submit again.",
    action: "Back to dashboard",
    href: "/",
  },
  429: {
    title: "Too many attempts",
    description: "Wait a moment, then try again.",
    action: "Back to sign in",
    href: "/",
  },
  500: {
    title: "Something went wrong",
    description: "We recorded the problem for the monitoring log. Try again shortly.",
    action: "Back to dashboard",
    href: "/",
  },
  502: {
    title: "The service is unreachable",
    description: "Wait a moment and try again.",
    action: "Try again",
    href: "/",
  },
  503: {
    title: "Temporarily unavailable",
    description: "The console is down for a short time. Try again shortly.",
    action: "Try again",
    href: "/",
  },
  504: {
    title: "The service took too long",
    description: "Try again in a moment.",
    action: "Try again",
    href: "/",
  },
};

function errorPageFor(code: number) {
  return HTTP_ERROR_PAGES[code] ?? HTTP_ERROR_PAGES[500];
}

export function AdminErrorPage({ code }: { code?: number }) {
  const params = useParams();
  const parsed = code ?? Number(params.code);
  const status = Number.isInteger(parsed) && parsed >= 400 && parsed < 600 ? parsed : 404;
  const page = errorPageFor(status);
  return (
    <main className="admin-frame">
      <Card>
        <EmptyState
          title={`${status} · ${page.title}`}
          description={page.description}
          action={
            <Link to={page.href}>
              <Button variant="primary">{page.action}</Button>
            </Link>
          }
        />
      </Card>
    </main>
  );
}
